import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import type { HomeProfile, HomeScope } from "../types/home.types";
import {
  formatLongDay,
  greeting,
  HOME_PROFILE_LABELS,
  HOME_SCOPE_LABELS,
  HOME_SCOPES,
} from "../utils/homeFilters";
import { HOME_COLORS } from "./homeTheme";

interface HomeHeaderProps {
  userName?: string;
  today?: string;
  profile?: HomeProfile;
  defaultScope?: HomeScope;
  allowedScopes?: HomeScope[];
  scope?: HomeScope;
  onScopeChange(scope: HomeScope): void;
  canCreateProject: boolean;
  onNewProject(): void;
}

export function HomeHeader({
  userName,
  today,
  profile,
  defaultScope,
  allowedScopes,
  scope,
  onScopeChange,
  canCreateProject,
  onNewProject,
}: HomeHeaderProps) {
  const firstName = userName?.trim().split(/\s+/)[0] ?? "";

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="flex-end"
      justifyContent="space-between"
      gap={2}
    >
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 500 }}>
          {greeting(new Date().getHours())}
          {firstName ? `, ${firstName}` : ""}
        </Typography>
        {today && (
          <Typography sx={{ color: HOME_COLORS.muted, fontSize: 14 }}>
            {formatLongDay(today)}. Veja o que precisa da sua atenção hoje.
          </Typography>
        )}
        {profile && defaultScope && (
          <Box
            component="span"
            sx={{
              alignSelf: "flex-start",
              mt: 0.5,
              fontSize: 11,
              fontWeight: 500,
              px: 1,
              py: "3px",
              borderRadius: "10px",
              bgcolor: HOME_COLORS.primaryLight,
              color: HOME_COLORS.primary,
            }}
          >
            Perfil {HOME_PROFILE_LABELS[profile]}, visão padrão:{" "}
            {HOME_SCOPE_LABELS[defaultScope]}
          </Box>
        )}
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope ?? null}
          aria-label="Escopo"
          onChange={(_, value: HomeScope | null) =>
            value && onScopeChange(value)
          }
          sx={{
            bgcolor: "#FFF",
            border: "1px solid #D5DBE3",
            borderRadius: "6px",
            p: "3px",
            gap: "2px",
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              borderRadius: "4px !important",
              m: 0,
            },
            "& .MuiToggleButton-root": {
              height: 34,
              px: "14px",
              textTransform: "none",
              fontSize: 13,
              lineHeight: 1,
              color: "#44506A",
            },
            "& .Mui-selected, & .Mui-selected:hover": {
              bgcolor: `${HOME_COLORS.primary} !important`,
              color: "#FFF !important",
              fontWeight: 500,
            },
          }}
        >
          {HOME_SCOPES.map((value) => (
            <ToggleButton
              key={value}
              value={value}
              disabled={!allowedScopes?.includes(value)}
            >
              {HOME_SCOPE_LABELS[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Button
          variant="contained"
          disableElevation
          disabled={!canCreateProject}
          onClick={onNewProject}
          sx={{
            // mesma altura da caixa do seletor (34 + 2×3 de respiro + 2 de borda)
            height: 42,
            px: 2.5,
            borderRadius: "6px",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            bgcolor: HOME_COLORS.primary,
            "&:hover, &:active": { bgcolor: HOME_COLORS.primaryDark },
          }}
        >
          Novo Projeto
        </Button>
      </Box>
    </Box>
  );
}
